import { cloneDeep } from 'lodash';
import { ApplicantCompanyType, CompanyStructure, State, TrustType } from '@lendi/core-constants';

import {
  APPLICATION_DEFAULT_NAME,
  ASSET_TYPE,
  EMPLOYMENT_BASIS,
  EMPLOYMENT_EMPLOYMENT_TYPE,
  EMPLOYMENT_TYPE,
  EXPENSE_TYPE,
  FIELD_VALUES,
  FREQUENCY,
  GOVT_BENEFIT_TYPE,
  INCOME_TYPE,
  LIABILITY_TYPE,
  PRIVATE_OR_PUBLIC,
  PROPERTY_EXPENSE_TYPE,
  STATE,
} from '@funnels/constants';
import {
  AssetIncome,
  BusinessFullDocIncome,
  BusinessLowDocIncome,
  CardLiability,
  CarLoanLiability,
  CustomerData,
  Expenses,
  FormValues,
  GiftsAsset,
  GovtBenefitIncome,
  HomeContentsAsset,
  HomeLoanLiability,
  HousingStatus,
  LineOfCreditLiability,
  MiscIncome,
  OtherIncome,
  OtherLiability,
  OverdraftLiability,
  PaygIncome,
  PersonalLoanLiability,
  PropertyAsset,
  SavingCheckAccountAsset,
  ServerApplicantFetch,
  ServerApplicationFetch,
  SharesAsset,
  StoreEmployment,
  SuperannuationAccountAsset,
  TermDepositAsset,
  VehicleAsset,
} from '@funnels/types';

import {
  getMockBrokerInterviews,
  getMockBrokerInterviewsGuarantor,
  getMockCompanies,
  getMockCreditReports,
  getMockFinancialSituations,
  getMockPostProductSelectContract,
  getMockProductPreferences,
  getMockRelatedParties,
} from './mockObjects';

export const SERVER_CUSTOMER: CustomerData = {
  id: 'customer-1',
  firstName: 'Gerald',
  lastName: 'Fitzgerald',
  mobileNumber: '+61412345678',
  email: 'someone@lendi.com.au',
  brand: 'Lendi',
  isDeleted: false,
  updatedAt: '2019-07-19T05:42:06.744Z',
  createdAt: '2019-07-19T05:42:06.744Z',
};

export const SERVER_APPLICATION1: ServerApplicationFetch = {
  id: 'bd4f84a7-0505-4200-b89f-d2531cb79d3a',
  name: 'example name 111',
  primaryApplicantId: 'applicant-1',
  hidden: false,
  preliminary: {
    primaryPurpose: FIELD_VALUES.REFINANCE,
    // Refinance
    refinancingReasons: [FIELD_VALUES.CONSOLIDATE_CASHOUT],
    consolidateAmount: 25000,
    cashoutAmount: 20000,
    urgency: FIELD_VALUES.URGENT,
    // New purchase
    purchaseReason: FIELD_VALUES.LOAN_APPROVAL,
    purchaseSituation: FIELD_VALUES.CONTRACTS_EXCHANGED,
    isSellingExistingProperty: false,
    // Borrowing power
    numberOfDependants: 1,
    eligibleForFHOG: FIELD_VALUES.YES,
    paymentType: FIELD_VALUES.PRINCIPAL_AND_INTEREST,
    desiredFeatures: [FIELD_VALUES.OFFSET_OR_REDRAW, FIELD_VALUES.FIXED_RATE, FIELD_VALUES.MOBILE_APP],
    hasSelfEmploymentProof: true,
    contributions: [
      {
        type: FIELD_VALUES.DEPOSIT,
        amount: 100000,
      },
    ],
  },
  updatedAt: '2018-07-19T05:42:06.744Z',
  createdAt: '2018-07-19T05:42:06.744Z',
};

export const SERVER_APPLICATION2: ServerApplicationFetch = {
  id: '1a3b7a07-fecc-4f1d-813b-6f09e6aea776',
  name: APPLICATION_DEFAULT_NAME,
  primaryApplicantId: 'applicant-1',
  hidden: false,
  preliminary: {
    primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
    // Refinance
    refinancingReasons: [FIELD_VALUES.CONSOLIDATE_CASHOUT],
    consolidateAmount: undefined,
    cashoutAmount: 100000,
    urgency: FIELD_VALUES.URGENT,
    // New purchase
    purchaseReason: FIELD_VALUES.LOAN_APPROVAL,
    purchaseSituation: FIELD_VALUES.CONTRACTS_EXCHANGED,
    isSellingExistingProperty: false,
    // Borrowing power
    numberOfDependants: 2,
    eligibleForFHOG: FIELD_VALUES.NO,
    paymentType: FIELD_VALUES.PRINCIPAL_AND_INTEREST,
    desiredFeatures: [FIELD_VALUES.FIXED_RATE],
    hasSelfEmploymentProof: true,
    contributions: [
      {
        type: FIELD_VALUES.DEPOSIT,
        amount: 80000,
      },
    ],
  },
  updatedAt: '2019-07-19T05:42:06.744Z',
  createdAt: '2019-07-19T05:42:06.744Z',
};

export const SERVER_APPLICATION3: ServerApplicationFetch = {
  id: 'ac418185-bc94-4723-b371-afc22fcd06c4',
  name: 'Application 3 - test rental',
  primaryApplicantId: 'applicant-1',
  hidden: false,
  preliminary: {
    primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
    // Refinance
    refinancingReasons: [FIELD_VALUES.CONSOLIDATE_CASHOUT],
    consolidateAmount: undefined,
    cashoutAmount: 100000,
    urgency: FIELD_VALUES.URGENT,
    // New purchase
    purchaseReason: FIELD_VALUES.LOAN_APPROVAL,
    purchaseSituation: FIELD_VALUES.CONTRACTS_EXCHANGED,
    isSellingExistingProperty: false,
    // Borrowing power
    numberOfDependants: 2,
    eligibleForFHOG: FIELD_VALUES.NO,
    paymentType: FIELD_VALUES.PRINCIPAL_AND_INTEREST,
    desiredFeatures: [FIELD_VALUES.FIXED_RATE],
    hasSelfEmploymentProof: true,
    contributions: [
      {
        type: FIELD_VALUES.DEPOSIT,
        amount: 80000,
      },
    ],
  },
  updatedAt: '2020-01-10T05:42:06.744Z',
  createdAt: '2020-01-01T05:42:06.744Z',
};

// This is the user (see customer ID)
export const SERVER_APPLICANT1: ServerApplicantFetch = {
  id: 'applicant-1',
  customerId: 'customer-1',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 70000,
  contact: {
    mobileNumber: '+61431453674',
    emailAddress: 'app1@gmail.com',
    currentAddress: {
      housingStatus: HousingStatus.OWN_HOME_MORTGAGE,
    },
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1978-09-15',
  gender: FIELD_VALUES.MALE,
  maritalStatus: FIELD_VALUES.UNKNOWN,
  maritalDetails: undefined,
  residencyStatus: FIELD_VALUES.UNKNOWN,
  countryOfBirth: 'Australia',
  cityOfBirth: 'Perth',
  citizenship: 'United Kingdom',
  mothersMaidenName: 'mothersMaidenName',
  dependants: [
    {
      age: 13,
      sharedWith: {
        applicantId: 'applicant-3',
      },
      isAddedOnThisApplicant: true,
    },
    {
      age: 10,
      isAddedOnThisApplicant: true,
    },
  ],
  firstName: SERVER_CUSTOMER.firstName,
  lastName: SERVER_CUSTOMER.lastName,
  title: FIELD_VALUES.MR,
  middleName: undefined,
};

export const SERVER_APPLICANT2: ServerApplicantFetch = {
  id: 'applicant-2',
  customerId: 'customer-2',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.FAIR,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 80000,
  contact: {
    mobileNumber: '+61432456214',
    emailAddress: 'app2@gmail.com',
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1998-02-12',
  gender: FIELD_VALUES.FEMALE,
  maritalStatus: FIELD_VALUES.MARRIED,
  maritalDetails: {
    applicantId: 'applicant-3',
  },
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'Nico',
  lastName: 'Doe',
  title: FIELD_VALUES.MISS,
  middleName: undefined,
};

export const SERVER_APPLICANT3: ServerApplicantFetch = {
  id: 'applicant-3',
  customerId: 'customer-3',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.FAIR,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 80000,
  contact: {
    mobileNumber: '+61432456233',
    emailAddress: 'app3@gmail.com',
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1992-02-12',
  gender: FIELD_VALUES.FEMALE,
  maritalStatus: FIELD_VALUES.MARRIED,
  maritalDetails: {
    applicantId: 'applicant-2',
  },
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [
    {
      age: 13,
      sharedWith: {
        applicantId: 'applicant-1',
      },
      isAddedOnThisApplicant: false,
    },
  ],
  firstName: 'J-free',
  lastName: 'Doh',
  title: FIELD_VALUES.MRS,
  middleName: undefined,
};

export const SERVER_APPLICANT4: ServerApplicantFetch = {
  id: 'applicant-4',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 80000,
  contact: {
    mobileNumber: '+61432456244',
    emailAddress: 'app4@gmail.com',
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1991-02-12',
  gender: FIELD_VALUES.MALE,
  maritalStatus: FIELD_VALUES.SINGLE,
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'Andre',
  lastName: 'Bee',
  title: FIELD_VALUES.MR,
  middleName: undefined,
};

export const SERVER_APPLICANT5: ServerApplicantFetch = {
  id: 'applicant-5',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 90000,
  contact: {
    mobileNumber: '+61432456299',
    emailAddress: 'app5@gmail.com',
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1990-02-12',
  gender: FIELD_VALUES.MALE,
  maritalStatus: FIELD_VALUES.SINGLE,
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'Neville',
  lastName: 'Longbottom',
  title: FIELD_VALUES.MR,
  middleName: undefined,
};

export const SERVER_APPLICANT6: ServerApplicantFetch = {
  id: 'applicant-6',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 100000,
  contact: {
    mobileNumber: '+61433436233',
    emailAddress: 'app6@gmail.com',
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1989-02-12',
  gender: FIELD_VALUES.FEMALE,
  maritalStatus: FIELD_VALUES.SINGLE,
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'Mary',
  lastName: 'Rose',
  title: FIELD_VALUES.MRS,
  middleName: undefined,
};

export const SERVER_APPLICANT7_GUARANTOR: ServerApplicantFetch = {
  id: 'applicant-7',
  customerId: 'customer-7',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 100000,
  contact: {
    mobileNumber: '+61430223456',
    emailAddress: 'guarantor_mock@gmail.com',
    currentAddress: {
      housingStatus: HousingStatus.OWN_HOME_MORTGAGE,
      startDate: '2022-01-02',
    },
  },
  applicantType: FIELD_VALUES.GUARANTOR,
  dateOfBirth: '1989-02-12',
  gender: FIELD_VALUES.MALE,
  maritalStatus: FIELD_VALUES.SINGLE,
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'John',
  lastName: 'Guarantor',
  title: FIELD_VALUES.MR,
  middleName: undefined,
};

export const SERVER_APPLICANT8_GUARANTOR: ServerApplicantFetch = {
  id: 'applicant-8',
  customerId: 'customer-8',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 100000,
  contact: {
    mobileNumber: '+61430223459',
    emailAddress: 'guarantor_mock@gmail.com',
  },
  applicantType: FIELD_VALUES.GUARANTOR,
  dateOfBirth: '1989-02-12',
  gender: FIELD_VALUES.FEMALE,
  maritalStatus: FIELD_VALUES.SINGLE,
  residencyStatus: FIELD_VALUES.PERMANENT_RESIDENT,
  dependants: [],
  firstName: 'Sally',
  lastName: 'Guarantor',
  title: FIELD_VALUES.MS,
  middleName: undefined,
};

export const SERVER_APPLICANT9: ServerApplicantFetch = {
  id: 'applicant-9',
  customerId: 'customer-9',
  entity: FIELD_VALUES.PERSON,
  creditStatus: FIELD_VALUES.EXCELLENT,
  hasSelfEmploymentProof: false,
  totalIncomeEstimate: 70000,
  contact: {
    mobileNumber: '+61431453674',
    emailAddress: 'app1@gmail.com',
    currentAddress: {
      housingStatus: HousingStatus.OWN_HOME_MORTGAGE,
      startDate: '2022-01-02',
    },
    previousAddresses: [
      {
        housingStatus: HousingStatus.RENTING,
        startDate: '2021-01-02',
        endDate: '2022-01-02',
      },
      {
        housingStatus: HousingStatus.BOARDING,
        startDate: '2014-01-03',
        endDate: '2021-01-02',
      },
    ],
  },
  applicantType: FIELD_VALUES.BORROWER,
  dateOfBirth: '1978-09-15',
  gender: FIELD_VALUES.MALE,
  maritalStatus: FIELD_VALUES.UNKNOWN,
  maritalDetails: undefined,
  residencyStatus: FIELD_VALUES.UNKNOWN,
  dependants: [
    {
      age: 13,
      sharedWith: {
        applicantId: 'applicant-3',
      },
      isAddedOnThisApplicant: true,
    },
    {
      age: 10,
      isAddedOnThisApplicant: true,
    },
  ],
  firstName: SERVER_CUSTOMER.firstName,
  lastName: SERVER_CUSTOMER.lastName,
  title: FIELD_VALUES.MR,
  middleName: undefined,
};

export const SERVER_APPLICANT10_TRUST: ServerApplicantFetch = {
  id: 'applicant-10',
  customerId: 'customer-10',
  entity: FIELD_VALUES.TRUST,
  applicantType: FIELD_VALUES.BORROWER,

  trustName: 'trustName-10',
  trustType: TrustType.Unit,
  registeredAddress: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  mailingAddress: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  industryCode: '09422',
  registeredDate: '2019-08-19',
  registeredCountry: 'Australia',
};

export const SERVER_APPLICANT11_COMPANY: ServerApplicantFetch = {
  id: 'applicant-11',
  customerId: 'customer-11',
  entity: FIELD_VALUES.COMPANY,
  applicantType: FIELD_VALUES.BORROWER,
  companyName: 'companyName-11',
  companyType: ApplicantCompanyType.Ltd,
  companyStructure: CompanyStructure.SoleTrader,
  industryCode: '8832720',
  abn: '25008526469',
  acn: '25008524332',
  isNotRegistered: true,
  registeredState: State.NSW,
  registeredDate: '2019-08-19',
};

export const SERVER_ASSET1: PropertyAsset = {
  id: 'asset-1',
  clientId: 'asset-1-client',
  // liabilityId: ['liability-1', 'liability-2'],
  // liabilityClientId: ['liability-1-client', 'liability-2-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.VACANT_LAND,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.5,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: undefined,
      frequency: FREQUENCY.QUARTERLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.MONTHLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.INVESTMENT_PROPERTY_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.YEARLY,
    },
  ],
  contractDetails: {
    contractDate: '2020-08-19',
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: FIELD_VALUES.VILLA,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};
export const SERVER_ASSET2: PropertyAsset = {
  id: 'asset-2',
  clientId: 'asset-2-client',
  // liabilityId: ['liability-3'],
  // liabilityClientId: ['liability-3-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.7,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.3,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: 200,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: 200,
      frequency: FREQUENCY.WEEKLY,
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
  isConstruction: true,
};
export const SERVER_ASSET3: PropertyAsset = {
  id: 'asset-3',
  clientId: 'asset-3-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: 500,
      frequency: FREQUENCY.WEEKLY,
      comments: 'some comment here',
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: true,
  toBePurchased: true,
};

export const SERVER_ASSET4: PropertyAsset = {
  id: 'asset-4',
  clientId: 'asset-4-client',
  // liabilityId: ['liability-4'],
  // liabilityClientId: ['liability-4-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: 600,
      frequency: FREQUENCY.WEEKLY,
      comments: 'some another comment',
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: true,
  toBePurchased: true,
};
export const SERVER_ASSET5: PropertyAsset = {
  id: 'asset-5',
  clientId: 'asset-5-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: false,
  toBePurchased: false,
};
export const SERVER_ASSET6: PropertyAsset = {
  id: 'asset-6',
  clientId: 'asset-6-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: false,
  toBePurchased: true,
};
export const SERVER_ASSET7: PropertyAsset = {
  id: 'asset-7',
  clientId: 'asset-7-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: false,
  toBePurchased: false,
};
export const SERVER_ASSET8: PropertyAsset = {
  id: 'asset-8',
  clientId: 'asset-8-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: false,
  toBePurchased: false,
};
export const SERVER_ASSET9: PropertyAsset = {
  id: 'asset-9',
  clientId: 'asset-9-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: false,
  toBePurchased: false,
};
export const SERVER_ASSET10: VehicleAsset = {
  id: 'asset-10',
  clientId: 'asset-10-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.VEHICLE,
  make: 'Subaru',
  model: 'Forester',
  year: 2017,
  estimatedValue: 100000,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.33333333,
      },
    ],
  },
};

export const SERVER_ASSET11: TermDepositAsset = {
  id: 'asset-11',
  clientId: 'asset-11-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.TERM_DEPOSIT,
  estimatedValue: 100000,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.33333333,
      },
    ],
  },
  financialInstitution: 'ANZ',
};

export const SERVER_ASSET12: SharesAsset = {
  id: 'asset-12',
  clientId: 'asset-12-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.SHARES,
  estimatedValue: 100000,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.33333333,
      },
    ],
  },
};

export const SERVER_ASSET13: GiftsAsset = {
  id: 'asset-13',
  clientId: 'asset-13-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.GIFTS,
  estimatedValue: 100000,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.33333333,
      },
    ],
  },
};

export const SERVER_ASSET14: HomeContentsAsset = {
  id: 'asset-14',
  clientId: 'asset-14-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.HOME_CONTENTS,
  estimatedValue: 100000,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.33333333,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.33333333,
      },
    ],
  },
};

export const SERVER_ASSET15: PropertyAsset = {
  id: '4020af67-a6a0-407f-95af-53b287a0d006',
  clientId: '4020af67-a6a0-407f-95af-53b287a0d006',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 666,
    buildPriceAmount: 6666,
  },
  estimatedValue: 666666,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.VACANT_LAND,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.5,
      },
    ],
  },
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  propertyType: FIELD_VALUES.VILLA,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};

export const SERVER_ASSET16: SavingCheckAccountAsset = {
  id: 'asset-16',
  clientId: 'asset-16-client',
  type: ASSET_TYPE.SAVING_CHECK_ACCOUNT,
  estimatedValue: 666666,
  financialInstitution: 'Commonwealth Bank',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.5,
      },
    ],
  },
};

// new purchase
export const SERVER_ASSET17: PropertyAsset = {
  id: 'asset-17',
  clientId: 'asset-17-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: 500,
      frequency: FREQUENCY.WEEKLY,
      comments: 'some comment here',
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: FIELD_VALUES.INVESTMENT,
  propertyType: FIELD_VALUES.UNIT_LESS_THAN_50M2,
  toBeUsedAsSecurity: true,
  toBePurchased: true,
};

// refinance - investment
export const SERVER_ASSET18: PropertyAsset = {
  id: 'asset-18',
  clientId: 'asset-18-client',
  // liabilityId: [],
  // liabilityClientId: [],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: 500,
      frequency: FREQUENCY.WEEKLY,
      comments: 'some comment here',
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: FIELD_VALUES.INVESTMENT,
  propertyType: FIELD_VALUES.UNIT_LESS_THAN_50M2,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};

export const SERVER_ASSET19: SuperannuationAccountAsset = {
  id: 'asset-16',
  clientId: 'asset-16-client',
  type: ASSET_TYPE.SUPERANNUATION,
  estimatedValue: 666666,
  financialInstitution: 'Commonwealth Bank',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

// security owned by guarantor
export const SERVER_ASSET20: PropertyAsset = {
  id: 'asset-20',
  clientId: 'asset-20-client',
  // liabilityId: ['liability-1', 'liability-2'],
  // liabilityClientId: ['liability-1-client', 'liability-2-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.VACANT_LAND,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-7',
        customerId: 'customer-7',
        percent: 1,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: undefined,
      frequency: FREQUENCY.QUARTERLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.MONTHLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.INVESTMENT_PROPERTY_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.YEARLY,
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: FIELD_VALUES.VILLA,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};

// security co-owned by borrowers and guarantors
export const SERVER_ASSET21: PropertyAsset = {
  id: 'asset-21',
  clientId: 'asset-21-client',
  // liabilityId: ['liability-1', 'liability-2'],
  // liabilityClientId: ['liability-1-client', 'liability-2-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.VACANT_LAND,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.34,
      },
      {
        applicantId: 'applicant-7',
        customerId: 'customer-7',
        percent: 0.33,
      },
      {
        applicantId: 'applicant-8',
        customerId: 'customer-8',
        percent: 0.33,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: undefined,
      frequency: FREQUENCY.QUARTERLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.MONTHLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.INVESTMENT_PROPERTY_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.YEARLY,
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: FIELD_VALUES.VILLA,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};

// security owned by multiple guarantors exclusively
export const SERVER_ASSET22: PropertyAsset = {
  id: 'asset-22',
  clientId: 'asset-22-client',
  // liabilityId: ['liability-1', 'liability-2'],
  // liabilityClientId: ['liability-1-client', 'liability-2-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.VACANT_LAND,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-7',
        customerId: 'customer-7',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-8',
        customerId: 'customer-8',
        percent: 0.5,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: undefined,
      frequency: FREQUENCY.QUARTERLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.MONTHLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.INVESTMENT_PROPERTY_RUNNING_COSTS,
      amount: undefined,
      frequency: FREQUENCY.YEARLY,
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: FIELD_VALUES.VILLA,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
};

// Other property with toBeSold true, multi-owner and expenses
export const SERVER_ASSET23: PropertyAsset = {
  id: 'asset-2',
  clientId: 'asset-2-client',
  // liabilityId: ['liability-3'],
  // liabilityClientId: ['liability-3-client'],
  type: ASSET_TYPE.PROPERTY,
  constructionDetails: {
    landEstimatedValue: 300000,
    buildPriceAmount: 200000,
  },
  estimatedValue: 700000,
  address: {
    postcode: '2000',
    state: STATE.NSW,
    formatted: '9/37 Pitt Street, Sydney, 2000, NSW',
    city: 'sydney',
    standard: {
      streetName: 'Pitt',
      streetNumber: '37',
      streetType: 'street',
      level: '9',
      unit: undefined,
    },
  },
  propertyStatus: FIELD_VALUES.ESTABLISHED,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.7,
      },
      {
        applicantId: 'applicant-3',
        customerId: 'customer-3',
        percent: 0.3,
      },
    ],
  },
  propertyExpenses: [
    {
      type: PROPERTY_EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: 200,
      frequency: FREQUENCY.WEEKLY,
    },
    {
      type: PROPERTY_EXPENSE_TYPE.BODY_CORPORATE_FEES_STRATA_FEES_AND_LAND_TAX,
      amount: 200,
      frequency: FREQUENCY.WEEKLY,
    },
  ],
  contractDetails: {
    estimatedSettlementDate: undefined,
    contractPriceAmount: undefined,
  },
  primaryPurpose: undefined,
  propertyType: undefined,
  toBeUsedAsSecurity: true,
  toBePurchased: false,
  isConstruction: true,
  toBeSold: true,
};

export const SERVER_LIABILITY1: HomeLoanLiability = {
  id: 'liability-1',
  clientId: 'liability-1-client',
  type: LIABILITY_TYPE.HOME_LOAN,
  assetId: ['asset-1'],
  assetClientId: ['asset-1-client'],
  clearingFromThisLoan: true,
  financialInstitution: 'This',
  fixedTermExpiryDate: '2023-06-10',
  repaymentAmount: 800,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.INTEREST_ONLY,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.5,
      },
    ],
  },
  remainingTermDuration: 22, // ALWAYS YEARS
  interestOnlyRemainingTerm: 3, // ALWAYS YEARS
  outstandingBalance: 50000,
  annualInterestRate: 5,
  creditLimit: 100000,
  financialInstitutionBsb: '062-538',
  financialInstitutionAccountNumber: '43876294',
};
export const SERVER_LIABILITY2: HomeLoanLiability = {
  id: 'liability-2',
  clientId: 'liability-2-client',
  type: LIABILITY_TYPE.HOME_LOAN,
  assetId: ['asset-1'],
  assetClientId: ['asset-1-client'],
  clearingFromThisLoan: true,
  financialInstitution: 'This',
  fixedTermExpiryDate: '2023-06-10',
  repaymentAmount: 400,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.PRINCIPAL_AND_INTEREST, // or INTEREST_ONLY
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.6,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.4,
      },
    ],
  },
  remainingTermDuration: 11, // ALWAYS YEARS
  interestOnlyRemainingTerm: 3, // ALWAYS YEARS
  outstandingBalance: 5000,
  annualInterestRate: 3,
  creditLimit: 10000,
};
export const SERVER_LIABILITY3: HomeLoanLiability = {
  id: 'liability-3',
  clientId: 'liability-3-client',
  type: LIABILITY_TYPE.HOME_LOAN,
  assetId: ['asset-2'],
  assetClientId: ['asset-2-client'],
  clearingFromThisLoan: true,
  financialInstitution: 'This',
  fixedTermExpiryDate: '2023-06-10',
  repaymentAmount: 400,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.PRINCIPAL_AND_INTEREST, // or INTEREST_ONLY
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.6,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.4,
      },
    ],
  },
  remainingTermDuration: 11, // ALWAYS YEARS
  interestOnlyRemainingTerm: 3, // ALWAYS YEARS
  outstandingBalance: 5000,
  annualInterestRate: 3,
  creditLimit: 10000,
};
export const SERVER_LIABILITY4: CardLiability = {
  id: 'liability-4',
  clientId: 'liability-4-client',
  assetId: [],
  assetClientId: [],
  type: LIABILITY_TYPE.CREDIT_CARD,
  outstandingBalance: 1000,
  creditLimit: 10000,
  financialInstitution: 'American Express',
  annualInterestRate: 18.5,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};
export const SERVER_LIABILITY5: OtherLiability = {
  id: 'liability-5',
  clientId: 'liability-5-client',
  assetId: [],
  assetClientId: [],
  type: LIABILITY_TYPE.OTHER,
  description: 'description',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  financialInstitution: 'ING Direct',
  creditLimit: 12345,
  outstandingBalance: 12345,
  repaymentAmount: 21,
  repaymentFrequency: 'MONTHLY',
  remainingTermDuration: 22,
};

export const SERVER_LIABILITY6: CarLoanLiability = {
  id: 'liability-6',
  clientId: 'liability-6-client',
  assetId: [],
  assetClientId: [],
  balloonRepaymentAmount: 1200,
  financialInstitution: 'american_express',
  outstandingBalance: 1200,
  remainingTerm: 1,
  repaymentAmount: 100,
  repaymentFrequency: FREQUENCY.MONTHLY,
  type: LIABILITY_TYPE.CAR_LOAN,
};

export const SERVER_LIABILITY7: PersonalLoanLiability = {
  id: 'liability-7',
  clientId: 'liability-7-client',
  assetId: [],
  assetClientId: [],
  type: LIABILITY_TYPE.PERSONAL_LOAN,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  financialInstitution: 'ANZ',
  creditLimit: 12345,
  repaymentAmount: 300,
  repaymentFrequency: 'FORTNIGHTLY',
};

export const SERVER_LIABILITY8: HomeLoanLiability = {
  id: 'liability-8',
  clientId: 'liability-8-client',
  type: LIABILITY_TYPE.HOME_LOAN,
  assetId: ['asset-18'],
  assetClientId: ['asset-18-client'],
  clearingFromThisLoan: true,
  financialInstitution: 'This',
  fixedTermExpiryDate: '2023-06-10',
  repaymentAmount: 400,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.PRINCIPAL_AND_INTEREST, // or INTEREST_ONLY
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.6,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.4,
      },
    ],
  },
  remainingTermDuration: 11, // ALWAYS YEARS
  interestOnlyRemainingTerm: 3, // ALWAYS YEARS
  outstandingBalance: 5000,
  annualInterestRate: 3,
  creditLimit: 10000,
};

export const SERVER_LIABILITY9: HomeLoanLiability = {
  id: 'liability-9',
  clientId: 'liability-9-client',
  type: LIABILITY_TYPE.HOME_LOAN,
  assetId: ['asset-1'],
  assetClientId: ['asset-1-client'],
  clearingFromThisLoan: true,
  financialInstitution: 'This',
  fixedTermExpiryDate: '2023-06-10',
  repaymentAmount: 800,
  repaymentFrequency: FREQUENCY.MONTHLY,
  repaymentTypeDetails: FIELD_VALUES.INTEREST_ONLY,
  percentOwned: {
    proportions: FIELD_VALUES.EQUAL,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 0.5,
      },
      {
        applicantId: 'applicant-2',
        customerId: 'customer-2',
        percent: 0.5,
      },
    ],
  },
  remainingTermDuration: 22, // ALWAYS YEARS
  interestOnlyRemainingTerm: 3, // ALWAYS YEARS
  outstandingBalance: 50000,
  annualInterestRate: 5,
  creditLimit: 100000,
};

export const SERVER_LIABILITY10_GUARANTOR: OverdraftLiability = {
  id: 'liability-10',
  clientId: 'liability-10-client',
  assetId: [],
  assetClientId: [],
  type: LIABILITY_TYPE.OVERDRAFT,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-7',
        customerId: 'customer-7',
        percent: 1,
      },
    ],
  },
  financialInstitution: 'ANZ',
  creditLimit: 12345,
  repaymentAmount: 300,
  repaymentFrequency: 'FORTNIGHTLY',
};

export const SERVER_LIABILITY11_GUARANTOR: LineOfCreditLiability = {
  id: 'liability-11',
  clientId: 'liability-11-client',
  assetId: [],
  assetClientId: [],
  type: LIABILITY_TYPE.LINE_OF_CREDIT,
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-8',
        customerId: 'customer-8',
        percent: 1,
      },
    ],
  },
  financialInstitution: 'ANZ',
  creditLimit: 12345,
  repaymentAmount: 300,
  repaymentFrequency: 'FORTNIGHTLY',
};

export const SERVER_INCOME1: PaygIncome = {
  id: 'income-1',
  clientId: 'income-1-client',
  type: INCOME_TYPE.PAYG_BASE_SALARY,
  amount: 10000,
  frequency: FREQUENCY.MONTHLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  employmentId: 'employment-1',
};

export const SERVER_INCOME2: PaygIncome = {
  id: 'income-2',
  clientId: 'income-2-client',
  type: INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME,
  amount: 600,
  frequency: FREQUENCY.WEEKLY,
  comments: 'comments-2',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  employmentId: 'employment-2',
};

export const SERVER_INCOME3: AssetIncome = {
  id: 'income-3',
  clientId: 'income-3-client',
  type: INCOME_TYPE.RENTAL_INCOME,
  amount: 1000,
  frequency: FREQUENCY.WEEKLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  assetId: 'asset-1-client',
};

export const SERVER_INCOME4: OtherIncome = {
  id: 'income-4',
  clientId: 'income-4-client',
  type: INCOME_TYPE.INVESTMENT_INCOME,
  amount: 1000,
  frequency: FREQUENCY.FORTNIGHTLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME5: GovtBenefitIncome = {
  id: 'income-5',
  clientId: 'income-5-client',
  type: INCOME_TYPE.GOVT_BENEFIT,
  benefitType: GOVT_BENEFIT_TYPE.FAMILY_TAX,
  amount: 5000,
  frequency: FREQUENCY.YEARLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME6: MiscIncome = {
  id: 'income-6',
  clientId: 'income-6-client',
  type: INCOME_TYPE.FOREIGN_INCOME,
  amount: 800,
  frequency: FREQUENCY.MONTHLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME7: MiscIncome = {
  id: 'income-7',
  clientId: 'income-7-client',
  type: INCOME_TYPE.CHILD_MAINTENANCE,
  amount: 180,
  frequency: FREQUENCY.WEEKLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME8: MiscIncome = {
  id: 'income-8',
  clientId: 'income-8-client',
  type: INCOME_TYPE.WORKERS_COMPENSATION,
  amount: 350,
  frequency: FREQUENCY.FORTNIGHTLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};
export const SERVER_INCOME9: MiscIncome = {
  id: 'income-9',
  clientId: 'income-9-client',
  type: INCOME_TYPE.SUPER_ANNUITY_PENSION,
  amount: 1350,
  frequency: FREQUENCY.MONTHLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME10: OtherIncome = {
  id: 'income-10',
  clientId: 'income-10-client',
  type: INCOME_TYPE.OTHER_INCOME,
  amount: 650,
  frequency: FREQUENCY.FORTNIGHTLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  description: 'from lottery',
};

export const SERVER_INCOME11: PaygIncome = {
  id: 'income-11',
  clientId: 'income-11-client',
  employmentId: 'employment-v2-1',
  employmentClientId: 'employment-v2-1',
  type: INCOME_TYPE.PAYG_BASE_SALARY,
  amount: 10000,
  frequency: FREQUENCY.MONTHLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
};

export const SERVER_INCOME12: BusinessFullDocIncome = {
  id: 'income-12',
  clientId: 'income-12-client',
  type: INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC,
  companyId: 'company-2',
  companyClientId: 'company-2',
  incomes: [
    {
      year: 2020,
      companyProfitBeforeTax: 1000000,
      nonRecurringIncome: 2000,
      addback: {
        depreciation: 10000,
        interest: 5000,
        lease: 1000,
      },
    },
    {
      year: 2019,
      companyProfitBeforeTax: 1500000,
      nonRecurringIncome: 2000,
      addback: {
        nonRecurringExpenses: 5000,
        salary: 80000,
        superannuationExcess: 20000,
        other: 1000,
      },
    },
  ],
};

export const SERVER_INCOME13: BusinessLowDocIncome = {
  id: 'income-13',
  clientId: 'income-13-client',
  type: INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC,
  companyId: 'company-3',
  companyClientId: 'company-3',
  gross: 123456,
  bas: [
    {
      quarter: 1,
      salesAmount: 111,
    },
    {
      quarter: 2,
      salesAmount: 222,
    },
    {
      quarter: 3,
      salesAmount: 333,
    },
    {
      quarter: 4,
      salesAmount: 444,
    },
  ],
};

export const SERVER_INCOME14: AssetIncome = {
  id: 'income-14',
  clientId: 'income-14-client',
  type: INCOME_TYPE.RENTAL_INCOME,
  amount: 1000,
  frequency: FREQUENCY.WEEKLY,
  comments: 'comments-1',
  percentOwned: {
    proportions: FIELD_VALUES.SPECIFIED,
    owners: [
      {
        applicantId: 'applicant-1',
        customerId: 'customer-1',
        percent: 1,
      },
    ],
  },
  assetId: 'asset-17',
  assetClientId: 'asset-17-client',
};

export const SERVER_EMPLOYMENTS1: StoreEmployment = {
  id: 'employment-1',
  clientId: 'employment-1',
  businessName: 'ABC',
  abn: 25008526469,
  abnVerified: true,
  privateOrPublic: PRIVATE_OR_PUBLIC.PUBLIC,
  startDate: '2020-08-19',
  role: {
    anzsco: { code: '832211', category: 'LABOURERS', occupation: 'Product Assembler' },
    asco: { code: '9212-11', category: 'LABOURERS AND RELATED WORKERS', occupation: 'Product Assembler' },
  },
  foreignEmployed: false,
  employerAddress: {
    postcode: '2000',
    state: 'NSW',
    city: 'Sydney',
    country: 'Australia',
    formatted: '10/24 Pitt Street, Sydney, NSW 2000, Australia',
    standard: {
      streetName: 'Pitt Street',
      streetNumber: 'string',
      streetType: 'string',
      level: 'string',
      unit: 'string',
    },
  },
  onProbation: true,
  maternityLeaveReturnDate: '2020-08-19',
  type: EMPLOYMENT_TYPE.PRIMARY,
  employmentType: EMPLOYMENT_EMPLOYMENT_TYPE.EMPLOYEE,
  employmentBasis: EMPLOYMENT_BASIS.FULL_TIME,
  maternityLeaveReturnBasis: EMPLOYMENT_BASIS.FULL_TIME,
  applicantId: 'applicant-1',
};

export const SERVER_EMPLOYMENTS_V2_1: StoreEmployment = {
  id: 'employment-v2-1',
  clientId: 'employment-v2-1',
  applicantId: 'applicant-1',
  companyId: 'company-1',
  companyClientId: 'company-1',
  type: EMPLOYMENT_TYPE.PRIMARY,
  employmentType: EMPLOYMENT_EMPLOYMENT_TYPE.EMPLOYEE,
  applicationId: 'application-1',
  startDate: '2021-08-25',
  role: {
    anzsco: {
      code: 111312,
      category: 'MANAGERS',
      occupation: 'Member of Parliament',
    },
    asco: {
      code: '1111-11',
      category: 'MANAGERS AND ADMINISTRATORS',
      occupation: 'Parliamentarian or Councillor',
    },
  },
  onProbation: true,
  employmentBasis: EMPLOYMENT_BASIS.FULL_TIME,
  maternityLeaveReturnBasis: EMPLOYMENT_BASIS.FULL_TIME,
};

export const SERVER_EMPLOYMENTS_V2_2: StoreEmployment = {
  id: 'employment-v2-2',
  clientId: 'employment-v2-2',
  applicantId: 'applicant-2',
  companyId: 'company-2',
  companyClientId: 'company-2',
  type: EMPLOYMENT_TYPE.PRIMARY,
  employmentType: EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED,
  applicationId: 'application-1',
  startDate: '2021-08-25',
  role: {
    anzsco: {
      code: 111312,
      category: 'MANAGERS',
      occupation: 'Member of Parliament',
    },
    asco: {
      code: '1111-11',
      category: 'MANAGERS AND ADMINISTRATORS',
      occupation: 'Parliamentarian or Councillor',
    },
  },
  onProbation: true,
  employmentBasis: EMPLOYMENT_BASIS.FULL_TIME,
  maternityLeaveReturnBasis: EMPLOYMENT_BASIS.FULL_TIME,
};

export const SERVER_EMPLOYMENTS_V2_3: StoreEmployment = {
  id: 'employment-v2-3',
  clientId: 'employment-v2-3',
  applicantId: 'applicant-1',

  companyId: 'company-2',
  companyClientId: 'company-2',
  type: EMPLOYMENT_TYPE.SECONDARY,
  employmentType: EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED,
  applicationId: 'application-1',
  startDate: '2020-08-25',
  role: {
    anzsco: {
      code: 111312,
      category: 'ACCOUNTANT',
      occupation: 'ACCOUNTANT',
    },
    asco: {
      code: '1111-11',
      category: 'ACCOUNTANT',
      occupation: 'ACCOUNTANT',
    },
  },
  onProbation: false,
  employmentBasis: EMPLOYMENT_BASIS.FULL_TIME,
  maternityLeaveReturnBasis: EMPLOYMENT_BASIS.FULL_TIME,
};

export const SERVER_EMPLOYMENTS_V2_4: StoreEmployment = {
  id: 'employment-v2-4',
  clientId: 'employment-v2-4',
  applicantId: 'applicant-2',
  type: EMPLOYMENT_TYPE.SECONDARY,
  employmentType: EMPLOYMENT_EMPLOYMENT_TYPE.SELF_EMPLOYED,
  companyId: 'company-3',
  companyClientId: 'company-3',
  applicationId: 'application-1',
  startDate: '2020-08-25',
  role: {
    anzsco: {
      code: 111312,
      category: 'ACCOUNTANT',
      occupation: 'ACCOUNTANT',
    },
    asco: {
      code: '1111-11',
      category: 'ACCOUNTANT',
      occupation: 'ACCOUNTANT',
    },
  },
  onProbation: false,
  employmentBasis: EMPLOYMENT_BASIS.FULL_TIME,
  maternityLeaveReturnBasis: EMPLOYMENT_BASIS.FULL_TIME,
};

export const SERVER_EXPENSES1: Expenses = {
  applicantId: 'applicant-1',
  clientId: 'expenses-1-client',
  groupedApplicantId: '',
  expenses: [
    { type: EXPENSE_TYPE.LIFE_INSURANCE, amount: 123, frequency: FREQUENCY.WEEKLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.MEDICAL_AND_HEALTH, amount: 5432, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.INSURANCE, amount: 321, frequency: FREQUENCY.MONTHLY, comments: 'no comments' },
  ],
};

export const SERVER_EXPENSES2: Expenses = {
  applicantId: 'applicant-2',
  clientId: 'expenses-2-client',
  groupedApplicantId: '',
  expenses: [
    {
      type: EXPENSE_TYPE.PRIMARY_SECONDARY_EDUCATION,
      amount: 111,
      frequency: FREQUENCY.WEEKLY,
      comments: 'no comments',
    },
    { type: EXPENSE_TYPE.RENT, amount: 4444, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.TRANSPORT, amount: 400, frequency: FREQUENCY.MONTHLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.OTHER, amount: 4321, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
  ],
};

export const SERVER_EXPENSES3: Expenses = {
  applicantId: 'applicant-3',
  clientId: 'expenses-3-client',
  groupedApplicantId: '',
  expenses: [
    { type: EXPENSE_TYPE.PET_CARE, amount: 111, frequency: FREQUENCY.WEEKLY, comments: 'no comments' },
    {
      type: EXPENSE_TYPE.SECONDARY_RESIDENCE_RUNNING_COSTS,
      amount: 44544,
      frequency: FREQUENCY.YEARLY,
      comments: 'no comments',
    },
    {
      type: EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: 400,
      frequency: FREQUENCY.MONTHLY,
      comments: 'no comments',
    },
    { type: EXPENSE_TYPE.OTHER, amount: 1234, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
  ],
};

export const SERVER_EXPENSES4: Expenses = {
  applicantId: 'applicant-7',
  clientId: 'expenses-7-client',
  groupedApplicantId: '',
  expenses: [
    {
      type: EXPENSE_TYPE.TELEPHONE_INTERNET_PAY_TV_AND_MEDIA_STREAMING_SUBSCRIPTIONS,
      amount: 111,
      frequency: FREQUENCY.WEEKLY,
      comments: 'no comments',
    },
    {
      type: EXPENSE_TYPE.GROCERIES,
      amount: 44544,
      frequency: FREQUENCY.YEARLY,
      comments: 'no comments',
    },
    {
      type: EXPENSE_TYPE.PRIMARY_RESIDENCE_RUNNING_COSTS,
      amount: 400,
      frequency: FREQUENCY.MONTHLY,
      comments: 'no comments',
    },
    { type: EXPENSE_TYPE.OTHER, amount: 1234, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
  ],
};

export const SERVER_EXPENSES5: Expenses = {
  applicantId: 'applicant-8',
  clientId: 'expenses-8-client',
  groupedApplicantId: '',
  expenses: [
    {
      type: EXPENSE_TYPE.MEDICAL_AND_HEALTH,
      amount: 11331,
      frequency: FREQUENCY.WEEKLY,
      comments: 'no comments',
    },
    {
      type: EXPENSE_TYPE.CLOTHING_AND_PERSONAL_CARE,
      amount: 441544,
      frequency: FREQUENCY.YEARLY,
      comments: 'no comments',
    },
    {
      type: EXPENSE_TYPE.GROCERIES,
      amount: 1400,
      frequency: FREQUENCY.MONTHLY,
      comments: 'no comments',
    },
    { type: EXPENSE_TYPE.OTHER, amount: 1234, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
  ],
};

export const SERVER_EXPENSES6: Expenses = {
  applicantId: 'applicant-2',
  clientId: 'expenses-2-client',
  groupedApplicantId: '',
  expenses: [
    {
      type: EXPENSE_TYPE.PRIMARY_SECONDARY_EDUCATION,
      amount: 111,
      frequency: FREQUENCY.WEEKLY,
      comments: 'no comments',
    },
    { type: EXPENSE_TYPE.RENT, amount: 4444, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.RENT_UTILITIES, amount: 555, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.TRANSPORT, amount: 400, frequency: FREQUENCY.MONTHLY, comments: 'no comments' },
    { type: EXPENSE_TYPE.OTHER, amount: 4321, frequency: FREQUENCY.YEARLY, comments: 'no comments' },
  ],
};

export const SERVER_LEAD = {
  id: 'lead-1',
  customerId: 'customer-1',
  mobileNumber: 'string',
  generatedAt: 'string',
  timeZone: 'string',
  email: 'user@example.com',
  firstName: 'string',
  origin: 'SIGN_UP',
  brand: 'lendi',
  gclId: 'string',
  accountOrigin: 'string',
  utm: {
    utmSource: 'string',
    utmContent: 'string',
    utmMedium: 'string',
    utmCampaign: 'string',
    utmTerm: 'string',
  },
  data: {
    employmentType: 'EMPLOYEE',
    creditStatus: 'EXCELLENT',
    contractsExchanged: true,
    deposit: 0,
    desiredFeatures: ['FIXED_RATE'],
    hasSelfEmploymentProof: true,
    paymentType: 'INTEREST_ONLY',
    purpose: 'NEW_PURCHASE',
    purchaseSituation: 'CONTRACTS_EXCHANGED',
    refinancingReasons: ['CONSOLIDATE_CASHOUT'],
    preApproved: true,
    propertyUse: 'INVESTMENT',
    loanValue: 1000000,
    securityValue: 700000,
    consolidateAmount: 5000,
    cashoutAmount: 5000,
    urgency: 'URGENT',
    firstName: 'string',
    estimatedSettlementDate: '2021-01-29',
    annualInterestRate: 3.7,
    financialInstitution: 'string',
    numberOfBorrowers: 1,
    numberOfProperties: 1,
    buildPriceAmount: 0,
  },
};

export const BASE_DATA = {
  applications: [cloneDeep(SERVER_APPLICATION1), cloneDeep(SERVER_APPLICATION2), cloneDeep(SERVER_APPLICATION3)],
  applicants: [
    cloneDeep(SERVER_APPLICANT1),
    cloneDeep(SERVER_APPLICANT2),
    cloneDeep(SERVER_APPLICANT3),
    cloneDeep(SERVER_APPLICANT4),
    cloneDeep(SERVER_APPLICANT5),
    cloneDeep(SERVER_APPLICANT6),
  ],
  assets: [
    cloneDeep(SERVER_ASSET1),
    cloneDeep(SERVER_ASSET2),
    cloneDeep(SERVER_ASSET3),
    cloneDeep(SERVER_ASSET4),
    cloneDeep(SERVER_ASSET5),
    // cloneDeep(SERVER_ASSET6),
    // cloneDeep(SERVER_ASSET7),
    // cloneDeep(SERVER_ASSET8),
    // cloneDeep(SERVER_ASSET9),
    cloneDeep(SERVER_ASSET10),
    cloneDeep(SERVER_ASSET11),
    cloneDeep(SERVER_ASSET12),
    cloneDeep(SERVER_ASSET13),
    cloneDeep(SERVER_ASSET14),
    cloneDeep(SERVER_ASSET15),
    cloneDeep(SERVER_ASSET16),
  ],
  creditReports: getMockCreditReports(),
  employments: [cloneDeep(SERVER_EMPLOYMENTS1)],
  expenses: [cloneDeep(SERVER_EXPENSES1), cloneDeep(SERVER_EXPENSES2), cloneDeep(SERVER_EXPENSES3)],
  incomes: [
    cloneDeep(SERVER_INCOME1),
    cloneDeep(SERVER_INCOME2),
    cloneDeep(SERVER_INCOME3),
    cloneDeep(SERVER_INCOME4),
    cloneDeep(SERVER_INCOME5),
    cloneDeep(SERVER_INCOME6),
    cloneDeep(SERVER_INCOME7),
    cloneDeep(SERVER_INCOME8),
    cloneDeep(SERVER_INCOME9),
    cloneDeep(SERVER_INCOME10),
    cloneDeep(SERVER_INCOME11),
    cloneDeep(SERVER_INCOME12),
    cloneDeep(SERVER_INCOME13),
  ],
  liabilities: [
    cloneDeep(SERVER_LIABILITY1),
    cloneDeep(SERVER_LIABILITY2),
    cloneDeep(SERVER_LIABILITY3),
    cloneDeep(SERVER_LIABILITY4),
    cloneDeep(SERVER_LIABILITY5),
    cloneDeep(SERVER_LIABILITY7),
  ],
  brokerInterviews: getMockBrokerInterviews(),
  brokerInterviewsGuarantor: getMockBrokerInterviewsGuarantor(),
  financialSituations: getMockFinancialSituations(),
  productPreferences: getMockProductPreferences(),
  postProductSelect: getMockPostProductSelectContract(),
  relatedParties: getMockRelatedParties(),
  lead: cloneDeep(SERVER_LEAD),
  companies: getMockCompanies(),
  createdAt: '2018-09-28T10:55:51.603Z',
  updatedAt: '2018-09-28T10:55:51.603Z',
};

const applicationsRest = BASE_DATA.applications.filter((__, index) => index > 0);
export const DEMO_FORM_VALUE: { [key: string]: any } = {
  REFINANCE_DECREASE: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          refinancingReasons: [FIELD_VALUES.DECREASE_REPAYMENTS],
        },
      },
      ...applicationsRest,
    ],
  },
  REFINANCE_CONSOLIDATE_CASHOUT: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          refinancingReasons: [FIELD_VALUES.CONSOLIDATE_CASHOUT],
        },
      },

      ...applicationsRest,
    ],
  },
  REFINANCE_REBUILD: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          refinancingReasons: [FIELD_VALUES.CONSTRUCTION],
        },
      },

      ...applicationsRest,
    ],
  },
  NEW_PURCHASE_OFFER_MADE: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
          purchaseSituation: FIELD_VALUES.OFFER_MADE,
        },
      },

      ...applicationsRest,
    ],
  },
  NEW_PURCHASE_BUYING_SOON_NEW_BUILDING: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
          purchaseSituation: FIELD_VALUES.BUYING_SOON,
        },
      },

      ...applicationsRest,
    ],
    assets: [
      {
        ...BASE_DATA.assets[0],
        propertyStatus: FIELD_VALUES.NEW_BUILDING,
      },
    ],
  },
  NEW_PURCHASE_BUYING_SOON_VACANT_LAND: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
          purchaseSituation: FIELD_VALUES.BUYING_SOON,
        },
      },

      ...applicationsRest,
    ],
    assets: [
      {
        ...BASE_DATA.assets[0],
        propertyStatus: FIELD_VALUES.VACANT_LAND,
      },
    ],
  },
  BORROWING_POWER_OFFER_MADE: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
          purchaseReason: FIELD_VALUES.LOAN_AFFORDABILITY,
          purchaseSituation: FIELD_VALUES.OFFER_MADE,
        },
      },

      ...applicationsRest,
    ],
  },
  BORROWING_POWER_BUYING_SOON_VACANT_LAND: {
    ...BASE_DATA,
    applications: [
      {
        ...BASE_DATA.applications[0],
        preliminary: {
          ...BASE_DATA.applications[0].preliminary,
          primaryPurpose: FIELD_VALUES.NEW_PURCHASE,
          purchaseReason: FIELD_VALUES.LOAN_AFFORDABILITY,
          purchaseSituation: FIELD_VALUES.BUYING_SOON,
        },
      },

      ...applicationsRest,
    ],
    assets: [
      {
        ...BASE_DATA.assets[0],
        propertyStatus: FIELD_VALUES.VACANT_LAND,
      },
    ],
  },
};

const FUNNEL_ONE_FORM_VALUES = localStorage.getItem('funnel_one_form_values');

export const mockDataType = localStorage.getItem('_lendi_funnels_debug_mock_data') || 'REFINANCE_DECREASE';
export const MOCK_DATA: FormValues =
  localStorage.getItem('_lendi_funnels_debug_mock_data') === 'FUNNEL_ONE'
    ? FUNNEL_ONE_FORM_VALUES && JSON.parse(FUNNEL_ONE_FORM_VALUES)
    : DEMO_FORM_VALUE[mockDataType];
